import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import PublicShell from '../components/PublicShell.jsx';
import CubMascot from '../components/CubMascot.jsx';
import { Alert, Spinner } from '../components/ui.jsx';
import { useFieldCheck, countProblems } from '../lib/fields.jsx';

/**
 * Contact us.
 *
 * Public, because the people most likely to need it are the ones who
 * cannot get in: a parent whose child's account is locked has no way of
 * reaching anybody from behind a sign-in page.
 *
 * The topic list is fixed rather than free text so the inbox can be
 * sorted by the thing that decides urgency, and safeguarding is first
 * for the same reason - a message about a child should not have to be
 * found among forty about invoices. The server validates against the
 * same list; these are only its labels.
 */
const TOPICS = [
  { id: 'safeguarding', label: "Something about a child's safety" },
  { id: 'school', label: 'Bringing CyberCub to a school' },
  { id: 'account', label: 'Trouble signing in, or with an account' },
  { id: 'payment', label: 'A payment or an invoice' },
  { id: 'feedback', label: 'Feedback about the courses' },
  { id: 'other', label: 'Something else' },
];

const emailLooksRight = (value) => /^[^\s@]+@[^\s@.]+\.[^\s@]{2,}$/.test(value.trim());

const CHECKS = {
  name: (f) =>
    f.name.trim().length >= 2 ? null : 'Please tell us your name - at least two letters.',
  email: (f) =>
    emailLooksRight(f.email)
      ? null
      : 'We need an address to reply to. It needs an @ and a dot, like you@example.com.',
  topic: (f) => (f.topic ? null : 'Please choose what this is about.'),
  message: (f) =>
    f.message.trim().length >= 10
      ? null
      : 'Please say a little more, so we know how to help - at least ten characters.',
};
const FIELD_ORDER = ['name', 'email', 'topic', 'message'];

/* The server checks all of this again and answers with the same field
   names, so a refusal from it lands on the field it is about. */
const problemsFromServer = (err) => {
  const bad = {};
  for (const issue of err.issues ?? []) {
    if (issue.path && CHECKS[issue.path] && !bad[issue.path]) bad[issue.path] = issue.message;
  }
  return bad;
};

export default function Contact() {
  const { user } = useAuth();
  const fields = useFieldCheck(CHECKS, FIELD_ORDER);

  const [form, setForm] = useState({
    // Somebody already signed in has told us both of these once.
    name: user?.fullName ?? '',
    email: user?.userId?.includes('@') ? user.userId : '',
    topic: '',
    message: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = 'Contact us · CyberCub';
  }, []);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      fields.recheck(next);
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const bad = fields.checkAll(form);
    if (Object.keys(bad).length) {
      setError(countProblems(bad));
      return;
    }

    setBusy(true);
    try {
      await api.sendContactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic,
        message: form.message.trim(),
      });
      setSent(true);
    } catch (err) {
      fields.mark(problemsFromServer(err));
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicShell>
      <section className="about-head">
        <div className="about-head-text">
          <span className="home-eyebrow">Contact us</span>
          <h1>
            Questions, problems,
            <br />
            <span className="hl">or something we got wrong.</span>
          </h1>
          <p className="home-lede">
            Write to us here and a person will read it. Tell us enough to be useful - which account,
            which lesson, what happened - and we will come back to the address you leave.
          </p>
        </div>
        <CubMascot avatar="cub-sky" mood="wave" size={150} className="anim-float" />
      </section>

      <section className="home-section">
        {sent ? (
          <div className="contact-sent card">
            <CubMascot avatar="cub-mint" mood="star" size={110} />
            <h2>That is with us.</h2>
            <p>
              Thank you for writing. We read everything that arrives here, and we will reply to{' '}
              {/* Lower-cased, because that is the form it was stored in -
                  confirming an address back in a spelling we did not keep
                  invites somebody to wonder which one we have. */}
              <strong>{form.email.trim().toLowerCase()}</strong>.
            </p>
            <p className="small muted">
              If it was about a child&rsquo;s safety and something is happening right now, please do
              not wait for us - contact the police or a local child protection service.
            </p>
            <div className="home-cta">
              <Link to="/" className="btn">← Back to the home page</Link>
            </div>
          </div>
        ) : (
          <div className="contact-grid">
            <form className="contact-form card" onSubmit={onSubmit} noValidate>
              <h2 className="contact-form-title">Send a message</h2>

              {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

              <div className={fields.cls('name')}>
                <label htmlFor="name">🙂 Your name</label>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  placeholder="e.g. Priya Sharma"
                  value={form.name}
                  onChange={set('name')}
                  required
                  {...fields.flag('name')}
                />
                {fields.error('name')}
              </div>

              <div className={fields.cls('email')}>
                <label htmlFor="email">📧 Where we should reply</label>
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  placeholder="e.g. you@example.com"
                  value={form.email}
                  onChange={set('email')}
                  required
                  {...fields.flag('email')}
                />
                {fields.error('email')}
              </div>

              <div className={fields.cls('topic')}>
                <label htmlFor="topic">🏷️ What is this about?</label>
                <select
                  id="topic"
                  value={form.topic}
                  onChange={set('topic')}
                  required
                  {...fields.flag('topic')}
                >
                  <option value="">— choose one —</option>
                  {TOPICS.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                {fields.error('topic')}
              </div>

              <div className={fields.cls('message')}>
                <label htmlFor="message">✏️ Your message</label>
                <textarea
                  id="message"
                  rows={7}
                  maxLength={4000}
                  placeholder="Tell us what happened, and anything that would help us find it - an email address, the name of a lesson, what you saw on the screen."
                  value={form.message}
                  onChange={set('message')}
                  required
                  {...fields.flag('message')}
                />
                <div className="hint">{form.message.trim().length} of 4000 characters</div>
                {fields.error('message')}
              </div>

              <button
                type="submit"
                className={`btn btn-lg ${busy ? 'is-busy' : ''}`}
                style={{ width: '100%' }}
                aria-busy={busy}
                disabled={busy}
              >
                {busy ? (
                  <>
                    <Spinner />
                    Sending…
                  </>
                ) : (
                  'Send it 📨'
                )}
              </button>

              {busy && (
                <p className="form-busy" role="status">
                  Hang on - we are sending it. You only need to press the button once.
                </p>
              )}
            </form>

            <aside className="contact-aside">
              {/* A form on a children's site can receive a message about a
                  child in trouble. It is read by people during the day,
                  not watched by them, and saying so plainly is the only
                  honest thing to do - somebody who needs help tonight
                  should not be waiting on an inbox. */}
              <div className="contact-note is-urgent">
                <strong>⚠️ If a child is in danger right now</strong>
                <p>
                  Please do not use this form. Contact the police or your local child protection
                  service. Messages here are read by people during the day, not watched around the
                  clock.
                </p>
              </div>

              <div className="contact-note">
                <strong>🔑 Locked out of an account?</strong>
                <p>
                  If a school gave your child their User ID, their teacher can reset it faster than
                  we can. For an account you made yourself, write to us and say which email address
                  it uses.
                </p>
              </div>

              <div className="contact-note">
                <strong>🏫 Asking on behalf of a school?</strong>
                <p>
                  Choose <em>Bringing CyberCub to a school</em> and tell us roughly how many children
                  and which year groups. It saves a round of questions.
                </p>
              </div>

              <div className="contact-note">
                <strong>📬 What we do with it</strong>
                <p>
                  Your name, address and message are kept so we can answer you, and are read only by
                  the people who run CyberCub. Nothing here goes to anybody else.
                </p>
              </div>
            </aside>
          </div>
        )}
      </section>
    </PublicShell>
  );
}
